import { Button } from '@/components/UI/Common/Admin/Button';
import { Input } from '@/components/UI/Common/Admin/Input';
import {
  SearchableSelect,
  type SearchableSelectGroup,
} from '@/components/UI/Common/Admin/SearchableSelect';
import { PageLayout } from '@/components/UI/Layouts/Admin/PageLayout';
import axiosInstance from '@/config/axios';
import { ROUTES } from '@/config/routes';
import { useGetList } from '@/queries/admin';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import z from 'zod';
import { ImagesField } from './ImagesField';
import { AdminMessageRichEditor } from './AdminMessageRichEditor';
import { sendMessageSchema } from '@/config/validations';

type SendMessageFormData = z.infer<typeof sendMessageSchema>;

const defaultValues: SendMessageFormData = {
  text: '',
  author: '',
  communityName: '',
  chatSystemName: '',
  recipientEmail: '',
  recipientPhone: '',
  recipientEmployeeNumber: '',
  images: [],
};

export const SendMessagePage = () => {
  const listQueryParams = useMemo(
    () => ({
      sortBy: 'systemName',
      sortOrder: 'ASC' as const,
      page: 1,
      limit: 1000,
      title: '',
    }),
    [],
  );
  const { data: chatsResponse, isLoading: isChatsLoading } = useGetList('chats', listQueryParams);
  const { data: templatesResponse, isLoading: isTemplatesLoading } = useGetList(
    'chat-templates',
    listQueryParams,
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid, isSubmitting },
  } = useForm<SendMessageFormData>({
    resolver: zodResolver(sendMessageSchema),
    mode: 'onChange',
    defaultValues,
  });

  const recipientError = useMemo(
    () =>
      errors.recipientEmail?.message ||
      errors.recipientPhone?.message ||
      errors.recipientEmployeeNumber?.message,
    [
      errors.recipientEmail?.message,
      errors.recipientPhone?.message,
      errors.recipientEmployeeNumber?.message,
    ],
  );
  const chatsNames = useMemo(() => {
    return (chatsResponse?.data || []).map((chat) => {
      return { label: chat.title, value: chat.systemName };
    });
  }, [chatsResponse?.data]);

  const chatsTemplatesNames = useMemo(() => {
    return (templatesResponse?.data || []).map((template) => {
      return { label: template.title, value: template.systemName };
    });
  }, [templatesResponse?.data]);

  const chatGroups = useMemo(() => {
    const groups: SearchableSelectGroup[] = [];

    if (chatsNames.length) {
      groups.push({ label: 'Чаты', options: chatsNames });
    }
    if (chatsTemplatesNames.length) {
      groups.push({ label: 'Шаблоны чатов', options: chatsTemplatesNames });
    }

    return groups;
  }, [chatsNames, chatsTemplatesNames]);

  const resetForm = useCallback(() => {
    reset(defaultValues);
  }, [reset]);

  const onSubmit = async (data: SendMessageFormData) => {
    const formData = new FormData();
    formData.append('text', data.text.trim());
    formData.append('author', data.author.trim());
    formData.append('chatSystemName', data.chatSystemName.trim());

    if (data.communityName?.trim()) {
      formData.append('communityName', data.communityName.trim());
    }
    if (data.recipientEmail?.trim()) {
      formData.append('recipientEmail', data.recipientEmail.trim());
    }
    if (data.recipientPhone?.trim()) {
      formData.append('recipientPhone', data.recipientPhone.trim());
    }
    if (data.recipientEmployeeNumber?.trim()) {
      formData.append('recipientEmployeeNumber', data.recipientEmployeeNumber.trim());
    }
    data.images.forEach((file) => formData.append('images', file));

    try {
      await axiosInstance.post(
        `/user/chats/${encodeURIComponent(data.chatSystemName.trim())}/messages`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      toast.success('Сообщение успешно отправлено');
      resetForm();
    } catch (error) {
      console.error('Error sending admin message:', error);

      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Произошла ошибка при отправке';
      toast.error(message);
    }
  };

  const mergeFiles = useCallback((existingFiles: File[], incomingFiles: File[]) => {
    const uniqueFilesMap = new Map<string, File>();
    [...existingFiles, ...incomingFiles].forEach((file) => {
      uniqueFilesMap.set(`${file.name}-${file.size}-${file.lastModified}`, file);
    });
    return Array.from(uniqueFilesMap.values());
  }, []);

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Чаты', href: ROUTES.ADMIN.CHATS },
        { label: 'Отправка сообщения', href: '' },
      ]}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4">
        <Controller
          name="text"
          control={control}
          render={({ field }) => (
            <div className="w-full">
              <label
                className="mb-1 block text-sm font-medium text-gray-700"
                htmlFor="message-text"
              >
                Текст сообщения*
              </label>
              <AdminMessageRichEditor
                value={field.value}
                onChange={field.onChange}
                hasError={Boolean(errors.text?.message)}
                disabled={isSubmitting}
              />
              {errors.text?.message && (
                <p className="mt-1 text-sm text-red-500">{errors.text.message}</p>
              )}
            </div>
          )}
        />

        <Input {...register('author')} label="Автор" required error={errors.author?.message} />
        <Input
          {...register('communityName')}
          label="Название сообщества"
          error={errors.communityName?.message}
          placeholder="Необязательно"
        />
        <Controller
          name="chatSystemName"
          control={control}
          render={({ field, fieldState }) => (
            <SearchableSelect
              id="chat-system-name"
              label="Название чата"
              required
              value={field.value}
              onChange={field.onChange}
              groups={chatGroups}
              placeholder="Выберите системное имя"
              searchPlaceholder="Поиск по названию или системному имени..."
              loading={isChatsLoading || isTemplatesLoading}
              loadingText="Загрузка системных имён..."
              error={fieldState.error?.message}
            />
          )}
        />

        <Input
          {...register('recipientEmail')}
          label="Получатель (email)"
          error={errors.recipientEmail?.message}
          placeholder="Необязательно"
        />
        <Input
          {...register('recipientPhone')}
          label="Получатель (телефон)"
          error={errors.recipientPhone?.message}
          placeholder="Необязательно"
        />
        <Input
          {...register('recipientEmployeeNumber')}
          label="Получатель (табельный номер)"
          error={errors.recipientEmployeeNumber?.message}
          placeholder="Необязательно"
        />
        {recipientError && <p className="-mt-2 text-sm text-red-500">{recipientError}</p>}

        <Controller
          name="images"
          control={control}
          render={({ field }) => (
            <ImagesField
              files={field.value || []}
              onChange={field.onChange}
              mergeFiles={mergeFiles}
            />
          )}
        />

        <div className="flex justify-end gap-4">
          <Button
            variant="secondary"
            disabled={!isDirty || isSubmitting}
            onClick={resetForm}
            type="button"
          >
            Сбросить
          </Button>
          <Button type="submit" disabled={!isDirty || !isValid} loading={isSubmitting}>
            Отправить
          </Button>
        </div>
      </form>
    </PageLayout>
  );
};
